import { useCallback, useContext, useEffect, useMemo } from 'react';

import { AnimatedTransition } from '../../../../../../components/animated-transition';
import {
  DropdownFilteredList,
  IListItemValues,
} from '../../../../../../components/dropdown/dropdown-filtered-list';
import { PortfolioManagementContext } from '../../../../../../providers/portfolio-management';
import { RiskManagementContext } from '../../../../../../providers/risk-management/risk-management-provider';
import { CheckLastUpdate } from '../../../../components/check-last-update';

import './styles.scss';

interface IDropdownListRiskSnapshotsProps {
  timestamp?: string;
}

export const DropdownListRiskSnapshots = ({ timestamp }: IDropdownListRiskSnapshotsProps) => {
  const { selectedAccountOption } = useContext(PortfolioManagementContext);
  const {
    riskSnapshotsData,
    getLoadRiskReport,
    listRiskSnapshotsLoading,
    riskLoadings,
    selectedTimestampPerAccount,
    setSelectedTimestampPerAccount,
  } = useContext(RiskManagementContext);

  const { isSpotLadderQuoteLoading, isSpotLadderBaseLoading } = riskLoadings;
  const exchange = selectedAccountOption?.exchange;
  const accountName = selectedAccountOption?.name;

  const isDropdownLoading =
    isSpotLadderQuoteLoading || isSpotLadderBaseLoading || listRiskSnapshotsLoading;

  const riskSnapshotsDataValues: IListItemValues[] =
    riskSnapshotsData?.[exchange]?.[accountName] || [];
  const currentKey = `${exchange}-${accountName}`;

  useEffect(() => {
    if (listRiskSnapshotsLoading || !timestamp || !riskSnapshotsDataValues.length) return;
    const match = riskSnapshotsDataValues.find((v) => v.timestamp === timestamp);
    if (!match) {
      setSelectedTimestampPerAccount((prev) => ({
        ...prev,
        [currentKey]: riskSnapshotsDataValues[0],
      }));
    }
  }, [timestamp, listRiskSnapshotsLoading]);

  const handleDropdownFilteredListChange = useCallback(
    (value: IListItemValues) => {
      if (!value || value.timestamp === timestamp) return;

      getLoadRiskReport({ accounts: selectedAccountOption, timestamp: value.timestamp });
      setSelectedTimestampPerAccount((prev) => ({
        ...prev,
        [currentKey]: value,
      }));
    },
    [selectedAccountOption, timestamp],
  );

  const defaultForThisAccount =
    selectedTimestampPerAccount[currentKey] ?? riskSnapshotsDataValues[0];

  const renderDropdownFilteredList = useMemo(() => {
    return (
      <AnimatedTransition className="list-risk-snapshots">
        <DropdownFilteredList
          defaultValue={defaultForThisAccount}
          values={riskSnapshotsDataValues || []}
          onChange={handleDropdownFilteredListChange}
          className="list-risk-snapshots-select-timestamp"
          renderLabelSelected={
            <CheckLastUpdate
              lastUpdate={timestamp as string}
              // isLoading={isDropdownLoading}
              isClickable
            />
          }
          isShowTimeFilters
          isDropdownLoadingData={isDropdownLoading}
          timeFilters={['Today', 'Yesterday']}
          disabled={isDropdownLoading}
        />
      </AnimatedTransition>
    );
  }, [isDropdownLoading, riskSnapshotsDataValues, defaultForThisAccount, timestamp]);

  return renderDropdownFilteredList;
};
